import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  Megaphone, Calendar, Users, TrendingUp, Plus, Clock, 
  Star, DollarSign, Eye, Edit, ChevronRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function PractitionerDashboard() {
  const { user, token } = useAuth();
  const { currentColors } = useTheme();
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({
    totalSessions: 0,
    totalBookings: 0,
    totalViews: 0,
    upcomingBookings: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load sessions created by this practitioner
      const sessionsRes = await axios.get(`${API}/healing-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter sessions by this practitioner (if instructor matches user name)
      const mySessions = sessionsRes.data.filter(
        s => s.instructor?.toLowerCase() === user?.name?.toLowerCase()
      );
      setSessions(mySessions);
      
      setStats({
        totalSessions: mySessions.length,
        totalBookings: 0, // Would need booking data
        totalViews: 0, // Would need analytics
        upcomingBookings: 0
      });
    } catch (error) {
      console.error('Failed to load practitioner data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="practitioner-dashboard">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-2">
          <Megaphone className="w-8 h-8" style={{ color: currentColors.primary }} />
          <div>
            <h1 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
              My Practice
            </h1>
            <p style={{ color: currentColors.textMuted }}>
              Welcome back, {user?.name}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentColors.textMuted }}>My Sessions</p>
              <p className="text-2xl font-cinzel" style={{ color: currentColors.text }}>
                {stats.totalSessions}
              </p>
            </div>
            <Calendar className="w-8 h-8" style={{ color: currentColors.secondary }} />
          </div>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentColors.textMuted }}>Total Bookings</p>
              <p className="text-2xl font-cinzel" style={{ color: currentColors.text }}>
                {stats.totalBookings}
              </p>
            </div>
            <Users className="w-8 h-8" style={{ color: currentColors.accent }} />
          </div>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentColors.textMuted }}>Profile Views</p>
              <p className="text-2xl font-cinzel" style={{ color: currentColors.text }}>
                {stats.totalViews}
              </p>
            </div>
            <Eye className="w-8 h-8" style={{ color: currentColors.primary }} />
          </div>
        </div>
        
        <div className="glass-card p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentColors.textMuted }}>Upcoming</p>
              <p className="text-2xl font-cinzel" style={{ color: currentColors.text }}>
                {stats.upcomingBookings}
              </p>
            </div>
            <Clock className="w-8 h-8 text-green-400" />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link 
          to="/healing-sessions"
          className="glass-card p-6 flex items-center gap-4 hover:scale-[1.02] transition-all"
        >
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${currentColors.primary}20` }}
          >
            <Plus className="w-6 h-6" style={{ color: currentColors.primary }} />
          </div>
          <div className="flex-1">
            <h3 className="font-cinzel" style={{ color: currentColors.text }}>Create New Session</h3>
            <p className="text-sm" style={{ color: currentColors.textMuted }}>
              Add a new healing session to your offerings
            </p>
          </div>
          <ChevronRight className="w-5 h-5" style={{ color: currentColors.textMuted }} />
        </Link>
        
        <Link 
          to="/profile"
          className="glass-card p-6 flex items-center gap-4 hover:scale-[1.02] transition-all"
        >
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${currentColors.secondary}20` }}
          >
            <Edit className="w-6 h-6" style={{ color: currentColors.secondary }} />
          </div>
          <div className="flex-1">
            <h3 className="font-cinzel" style={{ color: currentColors.text }}>Edit Profile</h3>
            <p className="text-sm" style={{ color: currentColors.textMuted }}>
              Update your bio and practitioner details
            </p>
          </div>
          <ChevronRight className="w-5 h-5" style={{ color: currentColors.textMuted }} />
        </Link>
      </div>

      {/* My Sessions */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-cinzel" style={{ color: currentColors.primary }}>
            My Healing Sessions
          </h2>
          <Link 
            to="/healing-sessions"
            className="text-sm flex items-center gap-1 hover:opacity-80"
            style={{ color: currentColors.accent }}
          >
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div 
              className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: `${currentColors.primary}30`, borderTopColor: currentColors.primary }}
            />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 mx-auto mb-4" style={{ color: currentColors.textMuted }} />
            <p style={{ color: currentColors.textMuted }}>You haven't created any sessions yet</p>
            <Link 
              to="/healing-sessions"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg"
              style={{ backgroundColor: `${currentColors.primary}20`, color: currentColors.primary }}
            >
              <Plus className="w-4 h-4" />
              Create Your First Session
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sessions.slice(0, 6).map((session) => (
              <div 
                key={session.id} 
                className="p-4 rounded-lg border transition-all hover:opacity-80"
                style={{ 
                  backgroundColor: `${currentColors.background}50`,
                  borderColor: currentColors.border
                }}
              >
                <h3 className="font-cinzel mb-1" style={{ color: currentColors.text }}>
                  {session.title}
                </h3>
                <p className="text-sm line-clamp-2 mb-2" style={{ color: currentColors.textMuted }}>
                  {session.description}
                </p>
                <div className="flex items-center gap-2 text-xs">
                  <Clock className="w-3 h-3" style={{ color: currentColors.accent }} />
                  <span style={{ color: currentColors.textMuted }}>{session.duration} min</span>
                  <span 
                    className="ml-auto px-2 py-0.5 rounded-full capitalize"
                    style={{ 
                      backgroundColor: `${currentColors.secondary}20`,
                      color: currentColors.secondary
                    }}
                  >
                    {session.session_type}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Coming Soon Features */}
      <div 
        className="glass-card p-6"
        style={{ borderColor: `${currentColors.accent}30` }}
      >
        <h3 className="font-cinzel mb-3 flex items-center gap-2" style={{ color: currentColors.accent }}>
          <Star className="w-5 h-5" />
          Coming Soon
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm" style={{ color: currentColors.textMuted }}>
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" style={{ color: currentColors.primary }} />
            <span>Revenue tracking & payouts</span>
          </div>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" style={{ color: currentColors.primary }} />
            <span>Analytics dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" style={{ color: currentColors.primary }} />
            <span>Client management</span>
          </div>
        </div>
      </div>
    </div>
  );
}
